/**
 * LineItemForm.jsx
 * 3-Tab Modal for adding/editing a bulk sale line item as per Step 4 Process Flow:
 *   TAB 1: CUSTOMER / SUPPLIER DETAILS (Purchase type, Supplier search with limit/balance, PNR/Airline/Passenger, Attachments modal)
 *   TAB 2: SUPPLIER / CUSTOMER CHARGES (Custom ledger mapping dropdowns: Amount/Pct + COA ledger)
 *   TAB 3: PRICING BREAKDOWN (Purchase & Sale calculation blocks, Price By Quantity, Actions dropdowns, Real-time Net amounts)
 */
import React, { useState, useEffect, useRef } from "react";
import {
  FaTimes, FaChevronLeft, FaPaperclip, FaPlus, FaTrash,
  FaCalculator, FaPercent, FaChevronDown, FaUpload, FaCheck
} from "react-icons/fa";
import ChargesPopup from "./ChargesPopup";
import "./BulkSales.css";

const PURCHASE_TYPES = [
  { value: "choose",    label: "CHOOSE" },
  { value: "credit",    label: "CREDIT" },
  { value: "cash",      label: "CASH" },
  { value: "web_group", label: "WEB / GROUP" },
  { value: "supplier",  label: "SUPPLIER" },
  { value: "hotel",     label: "HOTEL" },
  { value: "bsp",       label: "BSP" },
  { value: "neutral",   label: "CUSTOMER (NEUTRAL)" },
];

const DEFAULT_COA_ACCOUNTS = [
  { id: 101, code: "0426-102", name: "0426-102 AIR TICKET" },
  { id: 102, code: "0410-101", name: "0410-101 HOTEL BOOKING" },
  { id: 103, code: "0420-101", name: "0420-101 VISA SERVICES" },
  { id: 104, code: "0430-101", name: "0430-101 INSURANCE" },
  { id: 105, code: "0440-101", name: "0440-101 BAGGAGE CHARGES" },
  { id: 106, code: "0450-101", name: "0450-101 REAL ESTATE" },
  { id: 107, code: "0499-101", name: "0499-101 MISCELLANEOUS INCOME" },
];

/** Typeahead component for supplier search */
function SupplierTypeahead({ value, onSelect }) {
  const [query, setQuery]       = useState(value?.display || value?.name || "");
  const [results, setResults]   = useState([]);
  const [open, setOpen]         = useState(false);
  const [selected, setSelected] = useState(value || null);
  const ref = useRef();

  useEffect(() => {
    if (value) {
      setSelected(value);
      if (value.display) setQuery(value.display);
      else if (value.name || value.full_name) {
        const code = value.ref_code || value.account_code || `SP-${value.id}`;
        setQuery(`${code}: ${value.name || value.full_name}`);
      }
    }
  }, [value]);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleSearch = async (q) => {
    setQuery(q);
    if (q.length < 2) { setResults([]); setOpen(false); return; }
    try {
      const r = await fetch(`/api/suppliers/?search=${encodeURIComponent(q)}`);
      const data = await r.json();
      const arr = Array.isArray(data) ? data : (data?.suppliers || data?.data || []);
      setResults(arr.slice(0, 12));
      setOpen(arr.length > 0);
    } catch { setResults([]); }
  };

  const select = (supplier) => {
    const code = supplier.ref_code || supplier.account_code || `SP-${supplier.id}`;
    const name = supplier.name || supplier.full_name || "";
    const display = `${code}: ${name}`;
    setQuery(display);
    setOpen(false);
    setSelected({ ...supplier, display });
    onSelect({ ...supplier, display });
  };

  const bal = parseFloat(selected?.account_balance || selected?.balance || 0);
  const balType = bal < 0 ? "Dr" : "Cr";
  const balAbs = Math.abs(bal).toFixed(2);
  const creditLimit = parseFloat(selected?.credit_limit || 0).toFixed(2);

  return (
    <div className="bs-typeahead-wrap" ref={ref}>
      <input
        className="bs-form-input"
        placeholder="Search by Account Code or Supplier Name (e.g. SP-B1-003 - AKBAR TRAVELS)..."
        value={query}
        onChange={e => handleSearch(e.target.value)}
        onFocus={() => results.length > 0 && setOpen(true)}
        style={{ fontWeight: selected ? 600 : 400 }}
      />
      {open && (
        <div className="bs-typeahead-dropdown">
          {results.map(s => {
            const code = s.ref_code || s.account_code || `SP-${s.id}`;
            const name = s.name || s.full_name || "";
            return (
              <div key={s.id} className="bs-typeahead-item" onClick={() => select(s)}>
                <span className="ref" style={{ fontWeight: 700, color: "#2563eb" }}>{code}</span>: <span className="name">{name}</span>
                {s.phone && <span className="mobile" style={{ color: "#64748b" }}> · {s.phone}</span>}
              </div>
            );
          })}
        </div>
      )}
      {selected && (
        <div className="bs-account-meta" style={{
          marginTop: 6, padding: "8px 12px", background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 6, display: "flex", gap: 16, fontSize: 12
        }}>
          <span>Credit Limit: <strong style={{ color: "#0f172a" }}>AED {creditLimit}</strong></span>
          <span style={{ color: balType === "Dr" ? "#dc2626" : "#16a34a", fontWeight: 600 }}>
            Account Balance: AED {balAbs} {balType}
          </span>
        </div>
      )}
    </div>
  );
}

/** Service-type-specific field groups */
function ServiceSpecificFields({ serviceType, data, onChange }) {
  const fld = (label, field, opts = {}) => (
    <div className="bs-form-field">
      <label>{label}{opts.required && <span className="required">*</span>}</label>
      {opts.textarea ? (
        <textarea
          className="bs-form-textarea"
          value={data[field] || ""}
          onChange={e => onChange(field, e.target.value)}
          rows={2}
          placeholder={opts.placeholder || ""}
        />
      ) : (
        <input
          className="bs-form-input"
          type={opts.type || "text"}
          value={data[field] || ""}
          onChange={e => onChange(field, e.target.value)}
          placeholder={opts.placeholder || ""}
        />
      )}
    </div>
  );

  const type = (serviceType || "").toLowerCase();

  if (type.includes("ticket") || type === "ticketing" || type.includes("air")) return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "0 14px" }}>
      {fld("PNR", "pnr", { placeholder: "e.g. ABC123", textarea: true })}
      {fld("AIRLINE", "airline", { placeholder: "e.g. Emirates / Qatar", textarea: true })}
      {fld("PASSENGER NAME", "passenger_name", { placeholder: "As per passport", textarea: true })}
      {fld("Flight Number", "flight_number")}
      {fld("From", "departure_city", { placeholder: "Departure city" })}
      {fld("To", "arrival_city", { placeholder: "Arrival city" })}
      {fld("Departure Date", "departure_date", { type: "date" })}
      {fld("Return Date", "return_date", { type: "date" })}
    </div>
  );

  if (type.includes("hotel")) return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "0 14px" }}>
      {fld("Hotel Name", "hotel_name", { placeholder: "Hotel name" })}
      {fld("Guest Name", "guest_name")}
      {fld("Check-in Date", "checkin_date", { type: "date" })}
      {fld("Check-out Date", "checkout_date", { type: "date" })}
      {fld("Room Type", "room_type")}
      {fld("Confirmation No.", "confirmation_no")}
    </div>
  );

  if (type.includes("visa")) return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "0 14px" }}>
      {fld("Applicant Name", "applicant_name")}
      {fld("Passport Number", "passport_no")}
      {fld("Visa Type", "visa_type", { placeholder: "e.g. Tourist 30 Days" })}
      {fld("Country", "country")}
      {fld("Application Date", "application_date", { type: "date" })}
      {fld("Remarks", "remarks", { textarea: true })}
    </div>
  );

  if (type.includes("insurance")) return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "0 14px" }}>
      {fld("Policy Number", "policy_number")}
      {fld("Insured Name", "insured_name")}
      {fld("Coverage Type", "coverage_type")}
      {fld("Start Date", "start_date", { type: "date" })}
      {fld("End Date", "end_date", { type: "date" })}
    </div>
  );

  // Generic / Others
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 14px" }}>
      {fld("Description", "description", { textarea: true })}
      {fld("Reference", "reference")}
    </div>
  );
}

export default function LineItemForm({ serviceType, onSave, onClose, initialData = null }) {
  // 3 Main Tabs: "details" (Tab 1), "ledger" (Tab 2), "pricing" (Tab 3)
  const [tab, setTab] = useState("details");
  const [validationError, setValidationError] = useState("");
  const [showAttachmentModal, setShowAttachmentModal] = useState(false);

  // Tab 1: Customer / Supplier Details
  const [purchaseType, setPurchaseType] = useState(
    initialData?.purchase_type || "credit"
  );
  const [supplier, setSupplier]         = useState(() => {
    if (initialData?.supplier) return initialData.supplier;
    if (initialData?.supplier_name || initialData?.supplier_id) {
      return {
        id: initialData.supplier_id || 3,
        name: initialData.supplier_name || "AKBAR TRAVELS OF INDIA",
        ref_code: "SP-B1-003",
        account_balance: 112732.00,
        credit_limit: 150000.00,
        display: initialData.supplier_name || "SP-B1-003 - AKBAR TRAVELS OF INDIA"
      };
    }
    // Default pre-selected supplier for Air Ticket / service configuration as per process flow
    return {
      id: 3,
      name: "AKBAR TRAVELS OF INDIA",
      ref_code: "SP-B1-003",
      account_balance: 112732.00,
      credit_limit: 150000.00,
      display: "SP-B1-003 - AKBAR TRAVELS OF INDIA"
    };
  });
  const [suppDueDate, setSuppDueDate]   = useState(initialData?.supplier_due_date || "");
  const [refLpo, setRefLpo]             = useState(initialData?.ref_lpo_no || "");
  const [supplierBillNo, setSupplierBillNo] = useState(initialData?.supplier_bill_no || "");
  const [exchangeRate, setExchangeRate] = useState(initialData?.exchange_rate || 1.0);
  const [typeSpecific, setTypeSpecific] = useState(
    initialData?.type_specific_data || {}
  );
  const [attachments, setAttachments]   = useState(initialData?.attachments || []);

  // Attachment Modal Sub-state
  const [attTitle, setAttTitle] = useState("Ticket Copy");
  const [selectedFile, setSelectedFile] = useState(null);

  // Tab 2: Supplier / Customer Charges & Ledger Mappings
  const [coaAccounts, setCoaAccounts] = useState(DEFAULT_COA_ACCOUNTS);
  const [supplierCharges, setSupplierCharges] = useState(() => {
    const list = (initialData?.charges || []).filter(c => c.side === "supplier");
    return list.length > 0 ? list : [{ id: 1, type: "AMOUNT", account: "", amount: "" }];
  });
  const [customerCharges, setCustomerCharges] = useState(() => {
    const list = (initialData?.charges || []).filter(c => c.side === "customer");
    return list.length > 0 ? list : [{ id: 1, type: "MARGIN", account: "0426-102 AIR TICKET", amount: "" }];
  });

  // Tab 3: Pricing Breakdown
  const [basePurchase, setBasePurchase] = useState(() => {
    const baseRow = (initialData?.charges || []).find(c => c.side === "supplier" && (c.charge_type === "amount" || c.charge_type === "base_cost"));
    return baseRow ? String(baseRow.amount) : String(initialData?.total_purchase_amount || "500.00");
  });
  const [baseSale, setBaseSale] = useState(() => {
    const baseRow = (initialData?.charges || []).find(c => c.side === "customer" && (c.charge_type === "amount" || c.charge_type === "base_sale"));
    return baseRow ? String(baseRow.amount) : String(initialData?.total_sale_amount || "550.00");
  });

  const [qtyMode, setQtyMode] = useState(false);
  const [unitPrice, setUnitPrice] = useState("500.00");
  const [quantity, setQuantity] = useState(1);

  const [purchaseAdditions, setPurchaseAdditions] = useState(() => {
    const rows = (initialData?.charges || []).filter(c => c.side === "supplier" && c.charge_type !== "amount" && c.charge_type !== "base_cost");
    return rows.length > 0 ? rows : [];
  });
  const [saleModifiers, setSaleModifiers] = useState(() => {
    const rows = (initialData?.charges || []).filter(c => c.side === "customer" && c.charge_type !== "amount" && c.charge_type !== "base_sale");
    return rows.length > 0 ? rows : [];
  });

  const [showPurMenu, setShowPurMenu] = useState(false);
  const [showSaleMenu, setShowSaleMenu] = useState(false);

  // Load COA accounts & supplier initial
  useEffect(() => {
    fetch("/api/coa/")
      .then(r => r.json())
      .then(data => {
        const arr = Array.isArray(data) ? data : (data?.data || []);
        if (arr.length > 0) setCoaAccounts(arr.map(c => ({ id: c.id, code: c.code || c.account_code, name: `${c.code || c.account_code || ''} ${c.name}` })));
      })
      .catch(() => {});

    if (initialData?.supplier_id && !supplier) {
      fetch(`/api/suppliers/${initialData.supplier_id}`)
        .then(r => r.json())
        .then(s => {
          if (s && s.id) {
            const code = s.ref_code || s.account_code || `SP-${s.id}`;
            const name = s.name || s.full_name || "";
            setSupplier({ ...s, display: `${code}: ${name}` });
          }
        })
        .catch(() => {});
    }
  }, [initialData]);

  // Pricing calculations
  const parseNum = v => parseFloat(v) || 0;

  const totalPurchase = Math.max(0, parseNum(basePurchase) + purchaseAdditions.reduce((acc, r) => {
    const amt = parseNum(r.amount);
    return r.charge_type === "commission_received" ? acc - amt : acc + amt;
  }, 0));

  const totalSale = Math.max(0, parseNum(baseSale) + saleModifiers.reduce((acc, r) => {
    const amt = parseNum(r.amount);
    return ["discount", "redeem"].includes(r.charge_type) ? acc - amt : acc + amt;
  }, 0));

  const netMargin = totalSale - totalPurchase;
  const marginPct = totalPurchase > 0 ? ((netMargin / totalPurchase) * 100).toFixed(1) : 0;

  const handleTypeSpecific = (field, val) =>
    setTypeSpecific(prev => ({ ...prev, [field]: val }));

  // Qty calculation
  const applyPriceByQty = () => {
    const calc = (parseNum(unitPrice) * parseNum(quantity)).toFixed(2);
    setBasePurchase(calc);
    setQtyMode(false);
  };

  // Attachment upload handler
  const handleUploadAttachment = () => {
    if (!selectedFile) return;
    const reader = new FileReader();
    reader.onload = () => {
      setAttachments(prev => [...prev, {
        id: Date.now(),
        file_url: reader.result,
        file_name: selectedFile.name,
        attachment_type: attTitle,
        mime_type: selectedFile.type,
      }]);
      setSelectedFile(null);
      setShowAttachmentModal(false);
    };
    reader.readAsDataURL(selectedFile);
  };

  // Save handler
  const handleSave = () => {
    setValidationError("");
    const typeName = typeof serviceType === "object" ? serviceType?.name : serviceType;
    if (!typeName) {
      setValidationError("Service Type is required.");
      return;
    }

    if (["supplier", "credit", "hotel", "bsp"].includes(purchaseType) && !supplier) {
      setValidationError("Supplier selection is required for the selected Purchase Type.");
      return;
    }

    // Build charges list
    const charges = [
      { side: "supplier", charge_type: "amount", amount: parseNum(basePurchase), description: "Base Purchase Cost" },
      ...purchaseAdditions.map(r => ({ side: "supplier", charge_type: r.charge_type, amount: parseNum(r.amount), description: r.description || null })),
      { side: "customer", charge_type: "amount", amount: parseNum(baseSale), description: "Base Sale Price" },
      ...saleModifiers.map(r => ({ side: "customer", charge_type: r.charge_type, amount: parseNum(r.amount), description: r.description || null }))
    ];

    const lineItem = {
      service_type:       typeName || "Air Ticket",
      purchase_type:      purchaseType,
      supplier_id:        supplier?.id || null,
      supplier_name:      supplier?.name || supplier?.full_name || supplier?.display || null,
      supplier_due_date:  suppDueDate || null,
      ref_lpo_no:         refLpo || null,
      supplier_bill_no:   supplierBillNo || null,
      exchange_rate:      parseFloat(exchangeRate) || 1.0,
      type_specific_data: typeSpecific,
      charges,
      attachments: attachments.filter(a => a.file_url),
      total_purchase_amount: totalPurchase,
      total_sale_amount:     totalSale,
    };
    onSave(lineItem);
  };

  const typeName = typeof serviceType === "object" ? serviceType?.name : serviceType;

  return (
    <>
      <div className="bs-svc-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
        <div className="bs-li-modal" style={{ maxWidth: 900, width: "95%" }}>
          {/* Header */}
          <div className="bs-li-modal-header">
            <span>{typeName || "Air Ticket"} — Service Detail Configuration</span>
            <button className="bs-svc-close-btn" onClick={onClose}><FaTimes /></button>
          </div>

          {/* 3 Main Tabs */}
          <div className="bs-li-tabs">
            <div
              className={`bs-li-tab${tab === "details" ? " active" : ""}`}
              onClick={() => setTab("details")}
            >
              TAB 1: CUSTOMER / SUPPLIER DETAILS
            </div>
            <div
              className={`bs-li-tab${tab === "ledger" ? " active" : ""}`}
              onClick={() => setTab("ledger")}
            >
              TAB 2: SUPPLIER / CUSTOMER CHARGES
            </div>
            <div
              className={`bs-li-tab${tab === "pricing" ? " active" : ""}`}
              onClick={() => setTab("pricing")}
            >
              TAB 3: PRICING BREAKDOWN
            </div>
          </div>

          {/* Body */}
          <div className="bs-li-body" style={{ maxHeight: "70vh", overflowY: "auto" }}>
            {validationError && (
              <div style={{ background: "#fef2f2", color: "#dc2626", border: "1px solid #fecaca", padding: "10px 14px", borderRadius: 6, marginBottom: 14, fontSize: 13, fontWeight: 600 }}>
                ⚠️ {validationError}
              </div>
            )}

            {/* ──────────────────────── TAB 1 ────────────────────────────── */}
            {tab === "details" && (
              <>
                <div className="bs-li-section-title">1. Passenger & Service Details Section</div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: "0 18px" }}>
                  <div className="bs-form-field">
                    <label>Purchase Type</label>
                    <select
                      className="bs-form-select"
                      value={purchaseType}
                      onChange={e => setPurchaseType(e.target.value)}
                    >
                      {PURCHASE_TYPES.map(t => (
                        <option key={t.value} value={t.value}>{t.label}</option>
                      ))}
                    </select>
                  </div>

                  <div className="bs-form-field">
                    <label>Supplier <span className="required">*</span></label>
                    <SupplierTypeahead value={supplier} onSelect={s => setSupplier(s)} />
                  </div>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: "0 14px" }}>
                  <div className="bs-form-field">
                    <label>Supplier Due Date</label>
                    <input type="date" className="bs-form-input" value={suppDueDate} onChange={e => setSuppDueDate(e.target.value)} />
                  </div>
                  <div className="bs-form-field">
                    <label>Ref LPO #</label>
                    <input className="bs-form-input" value={refLpo} onChange={e => setRefLpo(e.target.value)} placeholder="Ref LPO No." />
                  </div>
                  <div className="bs-form-field">
                    <label>Exchange Rate</label>
                    <input type="number" className="bs-form-input" value={exchangeRate} onChange={e => setExchangeRate(e.target.value)} step="0.0001" min="0.0001" />
                  </div>
                  <div className="bs-form-field">
                    <label>Supplier Bill No.</label>
                    <input className="bs-form-input" value={supplierBillNo} onChange={e => setSupplierBillNo(e.target.value)} placeholder="Bill No." />
                  </div>
                </div>

                <div className="bs-li-section-title" style={{ marginTop: 16 }}>{typeName || "Air Ticket"} Details</div>
                <ServiceSpecificFields serviceType={typeName} data={typeSpecific} onChange={handleTypeSpecific} />

                {/* 2. Attachment Upload Sub-section */}
                <div className="bs-li-section-title" style={{ marginTop: 18, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span>2. Attachment Upload Sub-section</span>
                  <button
                    type="button"
                    className="bs-btn bs-btn-accent bs-btn-sm"
                    onClick={() => setShowAttachmentModal(true)}
                  >
                    <FaPaperclip size={11} /> + ATTACHMENT
                  </button>
                </div>

                {attachments.length > 0 ? (
                  <div className="bs-att-list">
                    {attachments.map(att => (
                      <div key={att.id} className="bs-att-item">
                        <FaPaperclip size={11} color="var(--bs-accent)" />
                        <a href={att.file_url} target="_blank" rel="noreferrer">{att.file_name || "Attachment"}</a>
                        <span style={{ color: "var(--bs-muted)", fontSize: 11 }}>({att.attachment_type})</span>
                        <button style={{ marginLeft: "auto", background: "none", border: "none", cursor: "pointer", color: "var(--bs-danger)" }} onClick={() => setAttachments(prev => prev.filter(a => a.id !== att.id))}>✕</button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ fontSize: 12, color: "var(--bs-muted)", fontStyle: "italic", marginTop: 6 }}>
                    No attachments uploaded. Click <strong>+ ATTACHMENT</strong> to add ticket copy or documents.
                  </div>
                )}
              </>
            )}

            {/* ──────────────────────── TAB 2 ────────────────────────────── */}
            {tab === "ledger" && (
              <>
                <div className="bs-li-section-title">SUPPLIER / CUSTOMER CHARGES (Account Ledger Mapping)</div>
                <p style={{ fontSize: 12, color: "var(--bs-muted)", marginBottom: 16 }}>
                  Configure specific Chart of Accounts (COA) ledger mappings for supplier costing and customer sales margins.
                </p>

                {/* SUPPLIER CHARGES */}
                <div style={{ background: "#f8fafc", padding: 14, borderRadius: 8, border: "1px solid #e2e8f0", marginBottom: 16 }}>
                  <div style={{ fontWeight: 700, fontSize: 13, color: "#1e293b", marginBottom: 10, display: "flex", justifyContent: "space-between" }}>
                    <span>SUPPLIER CHARGES</span>
                    <button
                      className="bs-btn bs-btn-ghost bs-btn-sm"
                      onClick={() => setSupplierCharges(prev => [...prev, { id: Date.now(), type: "AMOUNT", account: "", amount: "" }])}
                    >
                      + Add Row
                    </button>
                  </div>

                  <table className="bs-charges-table">
                    <thead>
                      <tr>
                        <th>Type</th>
                        <th>Account (Ledger Mapping Dropdown)</th>
                        <th>Amount (AED)</th>
                        <th style={{ width: 40 }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {supplierCharges.map((row, idx) => (
                        <tr key={row.id || idx}>
                          <td style={{ width: 140 }}>
                            <select
                              value={row.type}
                              onChange={e => {
                                const v = e.target.value;
                                setSupplierCharges(prev => prev.map((r, i) => i === idx ? { ...r, type: v } : r));
                              }}
                            >
                              <option value="AMOUNT">AMOUNT</option>
                              <option value="PERCENTAGE">PERCENTAGE</option>
                            </select>
                          </td>
                          <td>
                            <select
                              value={row.account}
                              onChange={e => {
                                const v = e.target.value;
                                setSupplierCharges(prev => prev.map((r, i) => i === idx ? { ...r, account: v } : r));
                              }}
                            >
                              <option value="">-- Select COA Account Ledger --</option>
                              {coaAccounts.map(acc => (
                                <option key={acc.id} value={acc.code}>{acc.name}</option>
                              ))}
                            </select>
                          </td>
                          <td style={{ width: 140 }}>
                            <input
                              type="number"
                              placeholder="0.00"
                              value={row.amount}
                              onChange={e => {
                                const v = e.target.value;
                                setSupplierCharges(prev => prev.map((r, i) => i === idx ? { ...r, amount: v } : r));
                              }}
                            />
                          </td>
                          <td style={{ textAlign: "center" }}>
                            <button
                              style={{ background: "none", border: "none", color: "#ef4444", cursor: "pointer" }}
                              onClick={() => setSupplierCharges(prev => prev.filter((_, i) => i !== idx))}
                            >
                              <FaTrash size={11} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* CUSTOMER CHARGES */}
                <div style={{ background: "#f8fafc", padding: 14, borderRadius: 8, border: "1px solid #e2e8f0" }}>
                  <div style={{ fontWeight: 700, fontSize: 13, color: "#1e293b", marginBottom: 10, display: "flex", justifyContent: "space-between" }}>
                    <span>CUSTOMER CHARGES</span>
                    <button
                      className="bs-btn bs-btn-ghost bs-btn-sm"
                      onClick={() => setCustomerCharges(prev => [...prev, { id: Date.now(), type: "AMOUNT", account: "", amount: "" }])}
                    >
                      + Add Row
                    </button>
                  </div>

                  <table className="bs-charges-table">
                    <thead>
                      <tr>
                        <th>Type</th>
                        <th>Account (Ledger Mapping Dropdown)</th>
                        <th>Amount (AED)</th>
                        <th style={{ width: 40 }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {customerCharges.map((row, idx) => (
                        <tr key={row.id || idx}>
                          <td style={{ width: 140 }}>
                            <select
                              value={row.type}
                              onChange={e => {
                                const v = e.target.value;
                                setCustomerCharges(prev => prev.map((r, i) => i === idx ? { ...r, type: v } : r));
                              }}
                            >
                              <option value="MARGIN">MARGIN</option>
                              <option value="AMOUNT">AMOUNT</option>
                              <option value="PERCENTAGE">PERCENTAGE</option>
                            </select>
                          </td>
                          <td>
                            <select
                              value={row.account}
                              onChange={e => {
                                const v = e.target.value;
                                setCustomerCharges(prev => prev.map((r, i) => i === idx ? { ...r, account: v } : r));
                              }}
                            >
                              <option value="">-- Select COA Account Ledger --</option>
                              {coaAccounts.map(acc => (
                                <option key={acc.id} value={acc.code}>{acc.name}</option>
                              ))}
                            </select>
                          </td>
                          <td style={{ width: 140 }}>
                            <input
                              type="number"
                              placeholder="0.00"
                              value={row.amount}
                              onChange={e => {
                                const v = e.target.value;
                                setCustomerCharges(prev => prev.map((r, i) => i === idx ? { ...r, amount: v } : r));
                              }}
                            />
                          </td>
                          <td style={{ textAlign: "center" }}>
                            <button
                              style={{ background: "none", border: "none", color: "#ef4444", cursor: "pointer" }}
                              onClick={() => setCustomerCharges(prev => prev.filter((_, i) => i !== idx))}
                            >
                              <FaTrash size={11} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            {/* ──────────────────────── TAB 3 ────────────────────────────── */}
            {tab === "pricing" && (
              <>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                  
                  {/* A. Purchase Calculation Block */}
                  <div style={{ background: "#f8fafc", padding: 14, borderRadius: 8, border: "1px solid #e2e8f0" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                      <span style={{ fontWeight: 700, fontSize: 13, color: "#1e293b" }}>A. Purchase Calculation Block (Supplier Costing)</span>
                      <button
                        type="button"
                        className="bs-btn bs-btn-ghost bs-btn-sm"
                        onClick={() => setQtyMode(v => !v)}
                        style={{ fontSize: 11 }}
                      >
                        PRICE BY QUANTITY
                      </button>
                    </div>

                    {qtyMode && (
                      <div style={{ background: "#eff6ff", padding: 10, borderRadius: 6, marginBottom: 10, display: "flex", gap: 8, alignItems: "center" }}>
                        <div>
                          <label style={{ fontSize: 10 }}>Unit Price</label>
                          <input type="number" className="bs-form-input" style={{ height: 28 }} value={unitPrice} onChange={e => setUnitPrice(e.target.value)} />
                        </div>
                        <div>
                          <label style={{ fontSize: 10 }}>Qty</label>
                          <input type="number" className="bs-form-input" style={{ height: 28, width: 60 }} value={quantity} onChange={e => setQuantity(e.target.value)} />
                        </div>
                        <button className="bs-btn bs-btn-primary bs-btn-sm" style={{ marginTop: 14 }} onClick={applyPriceByQty}>Apply</button>
                      </div>
                    )}

                    <div className="bs-form-field" style={{ marginBottom: 10 }}>
                      <label>Base Purchase Amount (AED) <span style={{ color: "red" }}>*</span></label>
                      <input
                        type="number"
                        className="bs-form-input"
                        value={basePurchase}
                        onChange={e => setBasePurchase(e.target.value)}
                        step="0.01"
                        style={{ fontWeight: 700 }}
                      />
                    </div>

                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                      <span style={{ fontSize: 11, fontWeight: 600, color: "#64748b" }}>ACTIONS + Options (Purchase Additions)</span>
                      <div className="bs-dropdown-wrap">
                        <button
                          type="button"
                          className="bs-btn bs-btn-ghost bs-btn-sm"
                          onClick={() => setShowPurMenu(v => !v)}
                        >
                          + Add Addition <FaChevronDown size={8} />
                        </button>
                        {showPurMenu && (
                          <div className="bs-dropdown-menu" style={{ right: 0, left: "auto" }}>
                            <div className="bs-dropdown-item" onClick={() => { setPurchaseAdditions(prev => [...prev, { id: Date.now(), charge_type: "tax", amount: "" }]); setShowPurMenu(false); }}>TAX</div>
                            <div className="bs-dropdown-item" onClick={() => { setPurchaseAdditions(prev => [...prev, { id: Date.now(), charge_type: "commission_received", amount: "" }]); setShowPurMenu(false); }}>COMMISSION RECEIVED</div>
                          </div>
                        )}
                      </div>
                    </div>

                    <table className="bs-charges-table">
                      <tbody>
                        {purchaseAdditions.map((r, idx) => (
                          <tr key={r.id || idx}>
                            <td>{r.charge_type.toUpperCase().replace("_", " ")}</td>
                            <td>
                              <input type="number" placeholder="0.00" value={r.amount} onChange={e => {
                                const v = e.target.value;
                                setPurchaseAdditions(prev => prev.map((item, i) => i === idx ? { ...item, amount: v } : item));
                              }} />
                            </td>
                            <td style={{ width: 30 }}>
                              <button style={{ background: "none", border: "none", color: "#ef4444", cursor: "pointer" }} onClick={() => setPurchaseAdditions(prev => prev.filter((_, i) => i !== idx))}>✕</button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>

                    <div style={{ borderTop: "2px solid #cbd5e1", paddingTop: 8, marginTop: 8, display: "flex", justifyContent: "space-between", fontWeight: 700, fontSize: 13 }}>
                      <span>Total Purchase Amount:</span>
                      <span>AED {totalPurchase.toFixed(2)}</span>
                    </div>
                  </div>

                  {/* B. Sale Calculation Block */}
                  <div style={{ background: "#f8fafc", padding: 14, borderRadius: 8, border: "1px solid #e2e8f0" }}>
                    <div style={{ fontWeight: 700, fontSize: 13, color: "#1e293b", marginBottom: 10 }}>
                      B. Sale Calculation Block (Customer Pricing)
                    </div>

                    <div className="bs-form-field" style={{ marginBottom: 10 }}>
                      <label>Base Sale Amount (AED) <span style={{ color: "red" }}>*</span></label>
                      <input
                        type="number"
                        className="bs-form-input"
                        value={baseSale}
                        onChange={e => setBaseSale(e.target.value)}
                        step="0.01"
                        style={{ fontWeight: 700 }}
                      />
                    </div>

                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                      <span style={{ fontSize: 11, fontWeight: 600, color: "#64748b" }}>ACTIONS + Options (Sale Modifiers)</span>
                      <div className="bs-dropdown-wrap">
                        <button
                          type="button"
                          className="bs-btn bs-btn-accent bs-btn-sm"
                          onClick={() => setShowSaleMenu(v => !v)}
                        >
                          + Add Modifier <FaChevronDown size={8} />
                        </button>
                        {showSaleMenu && (
                          <div className="bs-dropdown-menu" style={{ right: 0, left: "auto" }}>
                            {["tax", "discount", "redeem", "loss_on_sale", "transaction_fee", "commission_payable"].map(m => (
                              <div key={m} className="bs-dropdown-item" onClick={() => { setSaleModifiers(prev => [...prev, { id: Date.now(), charge_type: m, amount: "" }]); setShowSaleMenu(false); }}>
                                {m.toUpperCase().replace(/_/g, " ")}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                    <table className="bs-charges-table">
                      <tbody>
                        {saleModifiers.map((r, idx) => (
                          <tr key={r.id || idx}>
                            <td>{r.charge_type.toUpperCase().replace(/_/g, " ")}</td>
                            <td>
                              <input type="number" placeholder="0.00" value={r.amount} onChange={e => {
                                const v = e.target.value;
                                setSaleModifiers(prev => prev.map((item, i) => i === idx ? { ...item, amount: v } : item));
                              }} />
                            </td>
                            <td style={{ width: 30 }}>
                              <button style={{ background: "none", border: "none", color: "#ef4444", cursor: "pointer" }} onClick={() => setSaleModifiers(prev => prev.filter((_, i) => i !== idx))}>✕</button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>

                    <div style={{ borderTop: "2px solid #cbd5e1", paddingTop: 8, marginTop: 8, display: "flex", justifyContent: "space-between", fontWeight: 700, fontSize: 13 }}>
                      <span>Total Sale Amount:</span>
                      <span style={{ color: "#16a34a" }}>AED {totalSale.toFixed(2)}</span>
                    </div>
                  </div>
                </div>

                {/* Net Margin summary */}
                <div style={{ marginTop: 14, padding: "10px 14px", background: netMargin >= 0 ? "#f0fdf4" : "#fef2f2", borderRadius: 6, display: "flex", justifyContent: "space-between", fontWeight: 700, fontSize: 13 }}>
                  <span>Net Calculated Profit Margin:</span>
                  <span style={{ color: netMargin >= 0 ? "#16a34a" : "#dc2626" }}>AED {netMargin.toFixed(2)} ({marginPct}%)</span>
                </div>
              </>
            )}
          </div>

          {/* Footer */}
          <div className="bs-li-footer">
            <div style={{ display: "flex", gap: 8 }}>
              {tab !== "details" && (
                <button className="bs-btn bs-btn-ghost" onClick={() => setTab(tab === "pricing" ? "ledger" : "details")}>
                  <FaChevronLeft size={11} /> Back
                </button>
              )}
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button className="bs-btn bs-btn-ghost" onClick={onClose}>CANCEL</button>
              {tab === "details" && (
                <button className="bs-btn bs-btn-accent" onClick={() => setTab("ledger")}>Next: Charges Mappings ›</button>
              )}
              {tab === "ledger" && (
                <button className="bs-btn bs-btn-accent" onClick={() => setTab("pricing")}>Next: Pricing Breakdown ›</button>
              )}
              <button className="bs-btn bs-btn-primary" onClick={handleSave}>
                SAVE
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Attachment Upload Modal */}
      {showAttachmentModal && (
        <div className="bs-svc-overlay" onClick={e => e.target === e.currentTarget && setShowAttachmentModal(false)}>
          <div className="bs-pricing-modal" style={{ maxWidth: 450, padding: 0 }}>
            <div className="bs-pricing-header">
              <span>+ ATTACHMENT</span>
              <button className="bs-svc-close-btn" onClick={() => setShowAttachmentModal(false)}><FaTimes /></button>
            </div>
            <div style={{ padding: 20 }}>
              <div className="bs-form-field">
                <label>Title</label>
                <select className="bs-form-select" value={attTitle} onChange={e => setAttTitle(e.target.value)}>
                  <option value="Ticket Copy">Ticket Copy</option>
                  <option value="Passport Copy">Passport Copy</option>
                  <option value="Visa Copy">Visa Copy</option>
                  <option value="Invoice">Invoice</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div className="bs-form-field" style={{ marginTop: 12 }}>
                <label>Choose File</label>
                <input
                  type="file"
                  className="bs-form-input"
                  onChange={e => setSelectedFile(e.target.files[0])}
                  accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                />
              </div>

              <div style={{ marginTop: 20, display: "flex", justifyContent: "flex-end", gap: 10 }}>
                <button className="bs-btn bs-btn-ghost" onClick={() => setShowAttachmentModal(false)}>Cancel</button>
                <button className="bs-btn bs-btn-primary" onClick={handleUploadAttachment} disabled={!selectedFile}>
                  <FaUpload size={11} /> UPLOAD
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
